import React, {useState} from 'react';
import Page from "components/Page";
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import Button from "components/Button";
import {StarIcon} from "@heroicons/react/outline";
import {CheckCircleIcon, PencilIcon, TrashIcon, XCircleIcon} from "@heroicons/react/solid";
import {Trail, TrailUpdateAPIData} from "types/Trail";
import {useNotifications} from "context/NotificationContext";
import DeleteTrail from "pages/Trail/Delete";
import {invalidate} from "services/query-client";
import Card from "components/Card";
import DescriptionList from "components/DescriptionList";
import DescriptionTerm from "components/DescriptionTerm";
import DescriptionDetails from "components/DescriptionDetails";
import DescriptionListItem from "components/DescriptionListItem";
import Badge from "../../../components/Badge";
import BooleanIndicator from "../../../components/BooleanIndicator";
import IconBadge from "../../../components/IconBadge";
import IconName from "../../../types/IconName";
import {toTitleCase} from "../../../services/string";
import StackedList from "../../../components/StackedList";
import StackedListItem from "../../../components/StackedListItem";
import Chip from "../../../components/Chip";
import {
  ChatAltIcon,
  ExternalLinkIcon,
  InboxIcon,
  PresentationChartLineIcon,
  ThumbDownIcon,
  ThumbUpIcon
} from "@heroicons/react/outline";
import {useOperator} from "../../../context/OperatorContext";
import TrailType from "../../../types/TrailType";
import ConvertedValue from "../../../components/ConvertedValue";
import {UnitSystem} from "../../../types/UnitSystem";
import StatList from "../../../components/StatList";
import StatBox from "../../../components/StatBox";
import {FeedbackSubjectType} from "../../../types/FeedbackSubjectType";
import CommentList from "../../../components/CommentList";
import Paginator from "../../../components/Paginator";
import {useAuth} from "../../../context/AuthContext";
import NotAuthorised from "../../NotAuthorised";

// Helpers ----

function toPercentage(rate?: number): string {
  return (100 * Number(rate)).toFixed(1) + '%';
}

// Component ----

function ViewTrail() {
  const {termFor, settings} = useOperator();
  const auth = useAuth();
  const {notify} = useNotifications();
  const navigate = useNavigate();
  const {id} = useParams();

  const {data: trail, isLoading} = useQuery(
    ['trails', {id}],
    () => ngage.trails.get(Number(id)),
    {enabled: auth.can('trails:read')}
  );

  const [feedbackPage, setFeedbackPage] = useState<number>(1);
  const {data: feedback, isLoading: isLoadingFeedback} = useQuery(
    ['feedback', {subjectType: 'trail', subjectId: id, page: feedbackPage}],
    () => ngage.feedback.matching({
      subject_type: FeedbackSubjectType.TRAIL,
      subject_id: Number(id),
      has_comments: true,
      sort: '-created_at',
      page: feedbackPage,
    }),
    {enabled: !!trail, keepPreviousData: true}
  );

  const update = useMutation((data: TrailUpdateAPIData) => ngage.trails.update(parseInt(id as string), data));
  const [deletingTrail, setDeletingTrail] = useState<Trail | null>(null);

  // Prepare action handlers
  function handleDeleteTrail() {
    if (trail) setDeletingTrail(trail)
  }

  function handleEditTrail() {
    if (id) navigate(`/trails/${id}/edit`)
  }

  async function handlePublishTrail() {
    if (!trail) {
      return
    }

    const publishing = !trail.is_published

    try {
      await update.mutateAsync({is_published: publishing})
      await invalidate(['trails', {id}])

      if (publishing) {
        notify("success", `${termFor('Trail')} Published`, `${trail.name} has been published and can now be used`)
      } else {
        notify("success", `${termFor('Trail')} Unpublished`, `${trail.name} has been unpublished and cannot be used`)
      }
    } catch (error) {
      notify("error", `${termFor('Trail')} Not Updated`, `${trail.name} publish status could not be changed`)
    }
  }

  // Prevent unauthorised viewing
  if (auth.cannot('trails:read')) {
    return <NotAuthorised/>
  }

  // Prepare page props
  const title = trail?.name || `View ${termFor('Trail')}`

  const breadcrumbs = {
    'Content': '/',
    [termFor('Trails')]: '/trails',
    [trail?.name || `View ${termFor('Trail')}`]: `/trails/${id}`
  }

  const actions = <>
    <Button
      color={Button.colors.WHITE}
      icon={TrashIcon}
      onClick={handleDeleteTrail}
      disabled={auth.cannot('trails:write')}
    >
      Delete
    </Button>

    <Button
      color={Button.colors.PRIMARY}
      icon={PencilIcon}
      onClick={handleEditTrail}
      disabled={auth.cannot('trails:write')}
    >
      Edit
    </Button>

    <Button
      color={Button.colors.PRIMARY}
      icon={trail?.is_published ? XCircleIcon : CheckCircleIcon}
      onClick={handlePublishTrail}
      showSpinner={update.isLoading}
      disabled={auth.cannot('trails:write') && auth.cannot('trails:publish')}
    >
      {trail?.is_published ? 'Unpublish' : 'Publish'}
    </Button>
  </>

  return (
    <Page title={title} breadcrumbs={breadcrumbs} actions={actions} isLoading={isLoading}>

      {!!trail &&
        <>
          {/* Feedback Stats */}
          <section>
            <StatList>
              <StatBox
                name={'Avg. Feedback Score'}
                icon={StarIcon}
                stat={(trail.aggregated_feedback && trail.aggregated_feedback.count) ? trail.aggregated_feedback.mean_score.toFixed(1) : 'N/A'}
              />

              <StatBox
                name={'Positive Feedback'}
                icon={ThumbUpIcon}
                stat={(trail.aggregated_feedback && trail.aggregated_feedback.count) ? toPercentage(trail.aggregated_feedback.positive_rate) : 'N/A'}
              />

              <StatBox
                name={'Negative Feedback'}
                icon={ThumbDownIcon}
                stat={(trail.aggregated_feedback && trail.aggregated_feedback.count) ? toPercentage(trail.aggregated_feedback.negative_rate) : 'N/A'}
              />

              <StatBox
                name={'Feedback'}
                icon={InboxIcon}
                stat={trail.aggregated_feedback ? trail.aggregated_feedback.count.toLocaleString() : undefined}
              />

              <StatBox
                name={'Comments'}
                icon={ChatAltIcon}
                stat={trail.aggregated_feedback ? trail.aggregated_feedback.comment_count.toLocaleString() : undefined}
                url={'#feedback-comments'}
              />

              <StatBox
                name={'Feedback Trend'}
                icon={PresentationChartLineIcon}
                stat={trail.aggregated_feedback ? toTitleCase(trail.aggregated_feedback.category_trend) : undefined}
              />
            </StatList>
          </section>

          {/* Trail Information */}
          <section className="mt-4">
            <Card
              heading={`${termFor('Trail')} Information`}
              subheading={`${toTitleCase(trail.type)} ${termFor('Trail')}`}
            >
              <DescriptionList>
                <div className="sm:col-span-1">
                  <DescriptionTerm>Rating</DescriptionTerm>
                  <DescriptionDetails>
                    {trail.rating ?
                      <Badge
                        text={trail.rating.name}
                        colour={trail.rating.colour}
                        textColour={trail.rating.text_colour}
                      /> :
                      <Badge text="Unrated" colour="#bbbbbb"/>
                    }
                  </DescriptionDetails>
                </div>

                <div className="sm:col-span-1">
                  <DescriptionTerm>Rating Summary</DescriptionTerm>
                  <DescriptionDetails>
                    <p>{trail.rating_summary ?? 'Not specified'}</p>
                  </DescriptionDetails>
                </div>

                <div className="sm:col-span-1">
                  <DescriptionTerm>Accessibility</DescriptionTerm>
                  <DescriptionDetails>
                    {!!trail.accessibility_criteria.length && Object.values(trail.accessibility_criteria).map(criterion => (
                      <IconBadge
                        icon={IconName.HAND}
                        text={toTitleCase(criterion)}
                        color={IconBadge.colors.BRAND}
                        key={criterion}
                      />
                    ))}

                    {!trail.accessibility_criteria.length &&
                      <p>No criteria listed</p>
                    }
                  </DescriptionDetails>
                </div>

                <div className="sm:col-span-1">
                  <DescriptionTerm>Length</DescriptionTerm>
                  <DescriptionDetails>
                    <ConvertedValue
                      value={trail.length}
                      fromUnit={'meters'}
                      toUnit={settings?.localisation.length_units === UnitSystem.METRIC ? 'meters' : 'feet'}
                    />
                  </DescriptionDetails>
                </div>

                <div className="sm:col-span-1">
                  <DescriptionTerm>Published</DescriptionTerm>
                  <DescriptionDetails>
                    <BooleanIndicator value={trail.is_published}/>
                  </DescriptionDetails>
                </div>

                <div className="sm:col-span-1">
                  <DescriptionTerm>Featured</DescriptionTerm>
                  <DescriptionDetails>
                    <BooleanIndicator value={trail.is_featured}/>
                  </DescriptionDetails>
                </div>

                <div className="sm:col-span-2">
                  <DescriptionTerm>Mapped</DescriptionTerm>
                  <DescriptionDetails>
                    <BooleanIndicator value={trail.is_mapped}/>
                  </DescriptionDetails>
                </div>

                <div className="sm:col-span-1">
                  <DescriptionTerm>Transport Modes</DescriptionTerm>
                  <DescriptionDetails>
                    {trail.transport_modes.join(', ')}
                  </DescriptionDetails>
                </div>

                <div className="sm:col-span-1">
                  <DescriptionTerm>Automatic</DescriptionTerm>
                  <DescriptionDetails>
                    <BooleanIndicator value={trail.is_automatic}/>
                  </DescriptionDetails>
                </div>

                {trail.description &&
                  <div className="sm:col-span-2">
                    <DescriptionTerm>Description</DescriptionTerm>
                    <DescriptionDetails>
                      <p className="whitespace-pre-line">{trail.description}</p>
                    </DescriptionDetails>
                  </div>
                }

                <div className="sm:col-span-1">
                  <DescriptionTerm>Circuit</DescriptionTerm>
                  <DescriptionDetails>
                    <BooleanIndicator value={trail.is_circuit}/>
                  </DescriptionDetails>
                </div>

                <div className="sm:col-span-1">
                  <DescriptionTerm>Bi-Directional</DescriptionTerm>
                  <DescriptionDetails>
                    <BooleanIndicator value={trail.is_bidirectional}/>
                  </DescriptionDetails>
                </div>

                {trail.type === TrailType.GOLF &&
                  <div className="sm:col-span-1">
                    <DescriptionTerm>Par</DescriptionTerm>
                    <DescriptionDetails>
                      {trail.par}
                    </DescriptionDetails>
                  </div>
                }

                <div className="sm:col-span-2">
                  <DescriptionTerm>Policies</DescriptionTerm>
                  <DescriptionDetails>
                    {!!trail.policy_links.length && trail.policy_links.map(link => (
                      <div className="grid grid-cols-3 gap-4">
                        <Chip
                          key={link.id}
                          name={link.text}
                          href={link.url}
                          description={link.url}
                          icon={ExternalLinkIcon}
                        />
                      </div>
                    ))}

                    {!trail.policy_links.length && 'None specified'}
                  </DescriptionDetails>
                </div>

                <div className="sm:col-span-2">
                  <DescriptionTerm>Requirements</DescriptionTerm>
                  <DescriptionDetails>
                    {!!trail.requirements.length &&
                      <StackedList>
                        {trail.requirements.map(requirement => (
                          <StackedListItem key={requirement}>
                            {requirement}
                          </StackedListItem>
                        ))}
                      </StackedList>
                    }

                    {!trail.requirements.length && 'None specified'}
                  </DescriptionDetails>
                </div>

                <div className="sm:col-span-1">
                  <DescriptionTerm>Has Conclusion</DescriptionTerm>
                  <DescriptionDetails>
                    <BooleanIndicator value={!!trail.conclusion}/>
                  </DescriptionDetails>
                </div>
              </DescriptionList>
            </Card>
          </section>

          {/* Waypoints */}
          <section className="mt-4">
            {trail?.waypoints.map((waypoint, index) => (
              <div className="mb-4" key={waypoint.id}>
                <Card
                  heading={`${trail.type === TrailType.GOLF ? 'Hole' : 'Waypoint'} ${index + 1}: ${waypoint.name}`}
                  subheading={waypoint.id === trail?.start_waypoint?.id ? 'Trail Start' : (waypoint.id === trail?.end_waypoint?.id ? 'Trail End' : '')}
                >
                  <DescriptionList>

                    {/* Description */}
                    {waypoint.description &&
                      <DescriptionListItem span={2}>
                        <DescriptionTerm>Description</DescriptionTerm>
                        <DescriptionDetails>
                          <p className="whitespace-pre-line">{waypoint.description}</p>
                        </DescriptionDetails>
                      </DescriptionListItem>
                    }

                    <DescriptionListItem span={1}>
                      <DescriptionTerm>Elevation</DescriptionTerm>
                      <DescriptionDetails>
                        <ConvertedValue
                          value={waypoint.elevation}
                          fromUnit={'meters'}
                          toUnit={settings?.localisation.length_units === UnitSystem.METRIC ? 'meters' : 'feet'}
                        />
                      </DescriptionDetails>
                    </DescriptionListItem>

                    {(index !== 0 || trail.is_circuit) &&
                      <DescriptionListItem span={1}>
                        <DescriptionTerm>Distance from previous</DescriptionTerm>
                        <DescriptionDetails>
                          <ConvertedValue
                            value={waypoint.distance}
                            fromUnit={'meters'}
                            toUnit={settings?.localisation.length_units === UnitSystem.METRIC ? 'meters' : 'feet'}
                          />
                        </DescriptionDetails>
                      </DescriptionListItem>
                    }

                    {/*/!* Media *!/*/}
                    {/*{waypoint.media.length > 0?*/}
                    {/*    <DescriptionListItem span={2}>*/}
                    {/*        <DescriptionTerm>Media</DescriptionTerm>*/}
                    {/*        <DescriptionDetails>*/}
                    {/*            <Gallery media={waypoint.media} />*/}
                    {/*        </DescriptionDetails>*/}
                    {/*    </DescriptionListItem>*/}
                    {/*: null}*/}

                    {/*/!* Exhibits *!/*/}
                    {/*{waypoint.exhibits.length > 0?*/}
                    {/*    <DescriptionListItem span={2}>*/}
                    {/*        <DescriptionTerm>{termFor('Exhibits')}</DescriptionTerm>*/}
                    {/*        <DescriptionDetails>*/}
                    {/*            <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">*/}
                    {/*                {waypoint.exhibits.map((exhibit, index) => (*/}
                    {/*                    <li key={index}>*/}
                    {/*                        <Chip*/}
                    {/*                            name={exhibit.name}*/}
                    {/*                            description={exhibit.type.name}*/}
                    {/*                            href={`/exhibits/${exhibit.id}`}*/}
                    {/*                            backgroundSrc={exhibit.media[0]?.preview_url}*/}
                    {/*                        />*/}
                    {/*                    </li>*/}
                    {/*                ))}*/}
                    {/*            </ul>*/}
                    {/*        </DescriptionDetails>*/}
                    {/*    </DescriptionListItem>*/}
                    {/*: null}*/}

                  </DescriptionList>
                </Card>
              </div>
            ))}
          </section>

          {/* Comments */}
          <section id="feedback-comments" className="mt-4">
            <Card heading="Comments" isLoading={isLoadingFeedback}>
              <CommentList feedback={feedback?.data ?? []}/>

              {!!feedback?.data.length &&
                <div className="mt-4">
                  <Paginator data={feedback} setPage={setFeedbackPage} hideBorder/>
                </div>
              }
            </Card>
          </section>
        </>
      }

      {/* Delete confirmation modal */}
      <DeleteTrail
        trail={deletingTrail}
        show={!!deletingTrail}
        onCancel={() => setDeletingTrail(null)}
        onDelete={() => navigate('/trails')}
        onDeleteFail={() => setDeletingTrail(null)}
      />
    </Page>
  );
}

// Export ----

export default ViewTrail;